import { ExecutionResult } from 'graphql';
import { MutationResult } from '@apollo/react-common';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import {
  DeleteTimelineEntryMutation,
  DeleteTimelineEntryMutationVariables,
  ListTimelineEntriesQuery,
  MeasurementSystem,
} from '../../../types/__generated__';
import {
  readTimelineEntries,
  writeTimelineEntries,
} from './useListTimelineEntries';
import { useNotNullableContext } from '../useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import { updateActivityTotals } from './updateActivityTotals';
import mutation from './queries/deleteTimelineEntry.gql';

export default function useDeleteTimelineEntry(
  measurementSystem?: MeasurementSystem,
  onCompleted?: (data: DeleteTimelineEntryMutation) => void,
): [
  (id: string) => Promise<ExecutionResult<DeleteTimelineEntryMutation>>,
  MutationResult<DeleteTimelineEntryMutation>,
] {
  const client = useApolloClient();
  const { pageType, pageShortName, isPreview } = useNotNullableContext(
    PageMetaContext,
  );

  const [runMutation, mutationResult] = useMutation<
    DeleteTimelineEntryMutation,
    DeleteTimelineEntryMutationVariables
  >(mutation, {
    update(cache, { data }) {
      if (!data || !data.deleteTimelineEntry) {
        return;
      }

      const result = readTimelineEntries(
        pageType,
        pageShortName,
        cache,
        measurementSystem,
      );

      if (!result) {
        return;
      }

      const {
        previousPage,
        previousOwner,
        previousTimeline,
        previousPageInfo,
        previousTimelineNodes,
      } = result;

      const newTimelineData: ListTimelineEntriesQuery = {
        page: {
          ...previousPage,
          owner: previousOwner,
          timeline: {
            ...previousTimeline,
            pageInfo: previousPageInfo,
            totalCount: previousTimeline.totalCount - 1,
            nodes: previousTimelineNodes.filter(
              timelineEntry => timelineEntry.id !== data.deleteTimelineEntry,
            ),
          },
        },
      };

      writeTimelineEntries(pageType, pageShortName, newTimelineData, cache);
      updateActivityTotals(client, pageType, pageShortName, isPreview);
    },
    context: {
      credentials: 'include',
    },
    onCompleted,
  });

  const deleteTimelineEntry = (id: string) =>
    runMutation({
      variables: { input: { id } },
    });

  return [deleteTimelineEntry, mutationResult];
}
